import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { TransportState } from "@wr/ui";

/**
 * How a screen tells the chrome that **its** transport is down — S2 §13.2
 * part 2, LOCKED: *"`ConnectionBanner` takes an optional `action` prop; the
 * screen decides. A screen with no in-content remedy (lobby, profile, settings)
 * passes `Retry`. The component never invents its own button."*
 *
 * Before this existed, the banner could only see two things: `navigator.onLine`
 * and whether the last session refresh failed. Neither of them is the lobby's
 * socket. T-191 B2 is what that gap looks like from a player's seat — the phone
 * blinks, the content freezes at 60% and stops taking taps (§10.3, correctly),
 * the network comes back, `navigator.onLine` flips to `true`, the banner
 * congratulates itself and **disappears**, and the lobby stays frozen with
 * nothing on screen to press. Transport health was being reported by something
 * that did not know what the transport was.
 *
 * So the screen that owns a socket reports its own state, and the shell merges
 * that with the two device-level signals it already had (`useNetworkStatus`).
 * Two rules keep it honest:
 *
 *  - **A screen reports only while it is mounted.** Unmount clears it, so a
 *    banner can never outlive the screen that raised it.
 *  - **Exactly one recovery affordance.** The retry comes from the screen, and
 *    §11.3 is what stops it being duplicated in the content area.
 */

export interface ScreenTransport {
  /** `online` means "this screen has nothing to report". */
  state: TransportState;
  /** The screen's own recovery. `null` when it has none to offer. */
  retry: (() => void) | null;
  /**
   * Whether a screen is reporting **at all**.
   *
   * `state: "online", reporting: true` is a screen saying "my socket is fine".
   * `state: "online", reporting: false` is silence — nobody is reporting, which
   * is what unmounting produces. The chrome has to tell them apart: the first
   * is a recovery and earns §13.1's green "Back online." bar, the second is a
   * player navigating away from a broken screen, and announcing that the
   * network came back would be a bar making a claim nothing supports.
   */
  reporting: boolean;
  /**
   * **Is this screen's data feed stale?** — S2 §20.1 fact 1, and the signal the
   * **coin chip** dims on (S3 §20.4 / room spec §11.2 exception 3).
   *
   * It is reported separately from `state` because the two answer different
   * questions and one screen makes them differ. `state` is what the *banner*
   * renders, and a terminal room screen deliberately reports no banner action —
   * nothing there needs the network. But the wallet number on that same screen is
   * still not current, so **terminal screens do not exempt the chip**: at
   * `results` with a dropped socket the `PrizePanel` may say "45 is in your
   * wallet" while the chip has not moved yet. Bright, those two are a screen
   * disagreeing with itself; dimmed, they are two true statements.
   *
   * Why the screen reports it rather than the chrome deriving it: the balance feed
   * rides the screen's own authenticated socket (`wr:balance:v1:<players.id>` —
   * one socket, one channel), so the screen is the only thing that knows. **A
   * screen with no socket reports nothing and the chip does not dim** — that
   * falls out of the report, it is not a special case (§20.4).
   */
  stale: boolean;
  /**
   * **The screen has taken the whole viewport and the chrome must stand down**
   * — game spec §2.2, the single exception in the product.
   *
   * > **LOCKED — in `round`, none of the following render: the S2 top bar, S4's
   * > `RoomHeader` (heading, phase chip, money plate), and S4's `ActionBar`.**
   *
   * It rides on this provider rather than on a new one because it answers the
   * same question the rest of this object does — *what does the chrome do about
   * the screen that is mounted* — and because the banner is the thing being moved:
   * an immersive screen renders it itself, as an overlay (§4.6), so the shell must
   * not render a second one. A screen reports it only while mounted, so the top
   * bar cannot stay hidden past the round that hid it.
   */
  immersive: boolean;
}

const IDLE: ScreenTransport = {
  state: "online",
  retry: null,
  reporting: false,
  stale: false,
  immersive: false,
};

const ScreenTransportContext = createContext<ScreenTransport>(IDLE);
const PublishContext = createContext<(next: ScreenTransport) => void>(() => undefined);

export function TransportProvider({ children }: { children: ReactNode }) {
  const [screen, setScreen] = useState<ScreenTransport>(IDLE);

  const publish = useCallback((next: ScreenTransport) => {
    setScreen((current) =>
      current.state === next.state &&
      current.retry === next.retry &&
      current.reporting === next.reporting &&
      current.stale === next.stale &&
      current.immersive === next.immersive
        ? current
        : next,
    );
  }, []);

  return (
    <PublishContext.Provider value={publish}>
      <ScreenTransportContext.Provider value={screen}>{children}</ScreenTransportContext.Provider>
    </PublishContext.Provider>
  );
}

/** Read the active screen's transport. For the chrome, not for screens. */
export function useScreenTransport(): ScreenTransport {
  return useContext(ScreenTransportContext);
}

/**
 * Publish this screen's transport state, its recovery control and whether its
 * data feed is stale.
 *
 * `retry` must be stable (a `useCallback`), or the effect below republishes on
 * every render.
 *
 * `stale` defaults to *"whatever the banner state implies"*, which is right for
 * every screen whose content and chip freeze together (the lobby). The room
 * screen passes it explicitly, because there the two diverge: a terminal reports
 * no banner action and still has a wallet number that is not current (§11.2
 * exception 3).
 */
export function useReportTransport(
  state: TransportState,
  retry: (() => void) | null,
  stale: boolean = state === "reconnecting" || state === "offline",
  immersive = false,
): void {
  const publish = useContext(PublishContext);

  useEffect(() => {
    publish({ state, retry, reporting: true, stale, immersive });
    // A screen's report dies with the screen. The next one starts from silence.
    return () => publish(IDLE);
  }, [immersive, publish, retry, stale, state]);
}
